/**
 * Request Queue Service
 * 
 * Queues failed HTTP requests and automatically retries them after token refresh.
 * Prevents data loss during token refresh operations.
 * 
 * Issue: #202
 */

import { Injectable } from '@angular/core'
import {
  HttpRequest,
  HttpEvent,
  HttpClient,
  HttpHandler
} from '@angular/common/http'
import { Observable, throwError, Subject, timer } from 'rxjs'
import {
  filter,
  debounceTime,
  switchMap,
  take,
  retry,
  retryWhen,
  delay,
  takeWhile,
  catchError
} from 'rxjs/operators'

import { SessionStateService, SessionState } from '@/services/session-state.service'
import { TokenRefreshOrchestrator, RefreshSource } from '@/services/token-refresh-orchestrator.service'
import { ErrorClassifierService } from '@/services/error-classifier.service'
import { log, warn, error } from '@/util/logger'

export interface QueuedRequest {
  request: HttpRequest<any>
  retryCount: number
  timestamp: number
  id: string
  maxRetries: number
}

/**
 * Service for queuing and retrying failed HTTP requests.
 * Automatically retries requests after token refresh.
 */
@Injectable({ providedIn: 'root' })
export class RequestQueueService {
  private _queue: QueuedRequest[] = []
  private readonly _maxQueueSize = 50
  private readonly _defaultMaxRetries = 3
  private readonly _processQueue$ = new Subject<void>()

  constructor(
    private _http: HttpClient,
    private _refreshOrchestrator: TokenRefreshOrchestrator,
    private _sessionState: SessionStateService,
    private _errorClassifier: ErrorClassifierService
  ) {
    // Process queue when session becomes authenticated
    this._sessionState.state$
      .pipe(
        filter(s => s === SessionState.Authenticated),
        debounceTime(100) // Wait for token to be applied
      )
      .subscribe(() => {
        this._processQueue()
      })

    // Process queue when explicitly requested
    this._processQueue$.pipe(debounceTime(200)).subscribe(() => {
      this._processQueue()
    })
  }

  /**
   * Queue a request for retry
   */
  queueRequest(
    request: HttpRequest<any>,
    retryCount: number = 0,
    maxRetries: number = this._defaultMaxRetries
  ): Observable<HttpEvent<any>> {
    if (retryCount >= maxRetries) {
      warn(
        `[RequestQueue] Max retries (${maxRetries}) exceeded for request`,
        request.url
      )
      return throwError({ error: 'Max retries exceeded', request })
    }

    // Prevent queue overflow
    if (this._queue.length >= this._maxQueueSize) {
      const removed = this._queue.shift()
      warn(
        `[RequestQueue] Queue full, removing oldest request:`,
        removed?.request.url
      )
    }

    const queued: QueuedRequest = {
      request,
      retryCount,
      timestamp: Date.now(),
      id: `${Date.now()}-${Math.random()}`,
      maxRetries
    }

    this._queue.push(queued)
    log(
      `[RequestQueue] Queued request (${this._queue.length}/${this._maxQueueSize}):`,
      request.url,
      `(retry ${retryCount}/${maxRetries})`
    )

    // If we're not refreshing, try immediately
    if (this._sessionState.currentState !== SessionState.Refreshing) {
      return this._executeRequest(queued)
    }

    // Otherwise, wait for refresh to complete
    return this._sessionState.state$.pipe(
      filter(s => s === SessionState.Authenticated),
      take(1),
      switchMap(() => this._executeRequest(queued))
    )
  }

  /**
   * Execute a queued request
   */
  private _executeRequest(queued: QueuedRequest): Observable<HttpEvent<any>> {
    // Remove from queue
    const index = this._queue.findIndex(q => q.id === queued.id)
    if (index >= 0) {
      this._queue.splice(index, 1)
    }

    log(
      `[RequestQueue] Executing queued request:`,
      queued.request.url,
      `(attempt ${queued.retryCount + 1})`
    )

    return this._http.request(queued.request).pipe(
      catchError(err => {
        const classification = this._errorClassifier.classify(err)

        // If token expired, trigger refresh and re-queue
        if (classification.type === 'token_expired') {
          log(
            `[RequestQueue] Token expired, triggering refresh and re-queuing:`,
            queued.request.url
          )
          this._refreshOrchestrator.requestRefresh(
            RefreshSource.Interceptor,
            `Request failed: ${queued.request.url}`
          )
          return this.queueRequest(
            queued.request,
            queued.retryCount + 1,
            queued.maxRetries
          )
        }

        // If recoverable error, retry with backoff
        if (classification.recoverable && classification.action === 'retry') {
          const retryAfter = classification.retryAfter || 3000
          log(
            `[RequestQueue] Recoverable error, retrying after ${retryAfter}ms:`,
            queued.request.url
          )

          if (queued.retryCount < queued.maxRetries) {
            return timer(retryAfter).pipe(
              switchMap(() =>
                this.queueRequest(
                  queued.request,
                  queued.retryCount + 1,
                  queued.maxRetries
                )
              )
            )
          }
        }

        // Non-recoverable or max retries reached
        error(
          `[RequestQueue] Request failed permanently:`,
          queued.request.url,
          err
        )
        return throwError(err)
      })
    )
  }

  /**
   * Process all queued requests
   */
  private _processQueue(): void {
    if (this._queue.length === 0) {
      return
    }

    log(
      `[RequestQueue] Processing ${this._queue.length} queued requests`
    )

    const toProcess = [...this._queue]
    this._queue = []

    // Process requests sequentially to avoid overwhelming the server
    toProcess.forEach((queued, index) => {
      setTimeout(() => {
        this._executeRequest(queued).subscribe({
          error: err => {
            error(
              `[RequestQueue] Queued request failed:`,
              queued.request.url,
              err
            )
          }
        })
      }, index * 100) // Stagger requests by 100ms
    })
  }

  /**
   * Get current queue size
   */
  get queueSize(): number {
    return this._queue.length
  }

  /**
   * Clear the queue
   */
  clearQueue(): void {
    log(`[RequestQueue] Clearing ${this._queue.length} queued requests`)
    this._queue = []
  }

  /**
   * Manually trigger queue processing
   */
  processQueue(): void {
    this._processQueue$.next()
  }
}
